import { SortOrder } from '@/constants/request';
import { LIST_QUERY_CACHE_TTL_SECONDS, PROJECT_FILES_TAG } from '@/api/constants';
import { api } from '@/api/core';
import { queryMockProjectFiles } from './mockProjectFiles';
import { mockDelay } from './mockUtils';

export const PROJECT_FILE_STATUSES = ['Extracted', 'Processing', 'Failed'] as const;

export type ProjectFileStatus = (typeof PROJECT_FILE_STATUSES)[number];

export const PROJECT_FILE_SOURCES = ['Uploaded', 'AI generated'] as const;

export type ProjectFileSource = (typeof PROJECT_FILE_SOURCES)[number];

/** Shape assumed until the backend contract exists */
export interface ProjectFileData {
  fileId: string;
  fileName: string;
  description: string | null;
  /** Upper-case extension, e.g. "PDF". */
  fileType: string;
  sizeBytes: number;
  status: ProjectFileStatus;
  uploadedDatetime: string;
  source: ProjectFileSource;
  /** Set for AI-generated files; null once the chat thread is deleted. */
  sourceChatThreadId: string | null;
  sourceChatThreadTitle: string | null;
}

export interface ProjectFilesFilters {
  statuses?: ProjectFileStatus[];
  sources?: ProjectFileSource[];
  fileTypes?: string[];
}

export type GetProjectFilesRequest = {
  projectId: string;
  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: SortOrder;
  /** Matched against the filename only. */
  search?: string;
} & ProjectFilesFilters;

export interface GetProjectFilesResponse {
  files: ProjectFileData[];
  numberOfResults: number;
  /** Every file type in the project, for the Type filter. */
  fileTypes: string[];
}

const MOCK_RESPONSE_DELAY_MS = 400;

export const getProjectFilesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getProjectFiles: builder.query<GetProjectFilesResponse, GetProjectFilesRequest>({
      // The backend `GET /projects/{projectId}/files` endpoint does not exist yet: serve mock data (see mockProjectFiles.ts).
      queryFn: async (args) => {
        await mockDelay(MOCK_RESPONSE_DELAY_MS);
        const response = queryMockProjectFiles(args);

        return response ? { data: response } : { error: { status: 404, data: 'Project not found' } };
      },
      providesTags: (_result, _error, { projectId }) => [{ type: PROJECT_FILES_TAG, id: projectId }],
      keepUnusedDataFor: LIST_QUERY_CACHE_TTL_SECONDS,
    }),
  }),
});

export const { useGetProjectFilesQuery } = getProjectFilesApi;
